import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  User,
  Mail,
  Phone,
  Lock,
  MapPin,
  Building,
  GraduationCap,
  Camera,
  Heart,
  Calendar,
  Facebook,
  Users,
  Briefcase,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Upload,
  X,
} from "lucide-react";
import divisions from "../../data/division.json";
import district from "../../data/district.json";
import upazila from "../../data/upazila.json";
import union from "../../data/union.json";
import educationQualification from "../../data/educationQualification.json";
import api from "../../utils/axios.config";
import { toast } from "react-hot-toast";
import role from "../../data/role.json";
import Loading from "../Shared/Loading";
import { formatDateToDDMMYYYY } from "../../utils/functions";

function Signup() {
  const [districts, setDistricts] = useState([]);
  const [upazilas, setUpazilas] = useState([]);
  const [unions, setUnions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const fileInputRef = useRef(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm();

  const selectedDivision = watch("divisionId");
  const selectedDistrict = watch("districtId");
  const selectedUpazila = watch("upazilaId");
  const password = watch("password");

  const bloodGroup = ["a+", "a-", "b+", "b-", "ab+", "ab-", "o+", "o-"];
  const maritalStatus = [
    { id: "single", bn_name: "অবিবাহিত" },
    { id: "married", bn_name: "বিবাহিত" },
    { id: "divorced", bn_name: "তালাকপ্রাপ্ত" },
    { id: "widowed", bn_name: "বিধবা/বিপত্নীক" },
  ];

  useEffect(() => {
    const filteredDistricts = district.filter(
      (dis) => dis.division_id === selectedDivision
    );
    setDistricts(filteredDistricts);

    const filteredUpazilas = upazila.filter(
      (upa) => upa.district_id === selectedDistrict
    );
    setUpazilas(filteredUpazilas);

    const filteredUnions = union.filter(
      (uni) => uni.upazilla_id === selectedUpazila
    );
    setUnions(filteredUnions);
  }, [selectedDivision, selectedDistrict, selectedUpazila]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setValue("file", [file]);

      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setValue("file", []);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data) => {
    if (!acceptedTerms) {
      toast.error("আপনাকে আমাদের শর্তাবলী গ্রহণ করতে হবে");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();

    formData.append("image", data.file[0]);
    formData.append("name", data.name);
    if (data.nameEn) formData.append("nameEn", data.nameEn);
    if (data.father) formData.append("father", data.father);
    if (data.mother) formData.append("mother", data.mother);
    if (data.occupation) formData.append("occupation", data.occupation);
    formData.append("institute", data.institute);
    formData.append("designation", data.designation);
    formData.append("password", data.password);
    formData.append("confirmPassword", data.confirmPassword);
    formData.append("mobile", data.mobile);
    formData.append("email", data.email);
    formData.append("division", data.divisionId);
    formData.append("district", data.districtId);
    formData.append("upazila", data.upazilaId);
    if (data.unionId) formData.append("union", data.unionId);
    formData.append("facebookUrl", data.facebook);
    formData.append("bloodGroup", data.bloodGroup);
    formData.append("isAgreeDonateBlood", data.isAgreeDonateBlood);
    formData.append("maritalStatus", data.maritalStatus);

    if (data.lastBloodDonateDate) {
      formData.append(
        "lastBloodDonation",
        formatDateToDDMMYYYY(data.lastBloodDonateDate)
      );
    }
    if (data.dob) {
      formData.append("dob", formatDateToDDMMYYYY(data.dob));
    }

    formData.append("educationQualification", data.educationQualification);
    formData.append("nid", data.nid);
    formData.append("role", "member");

    try {
      const { data: response } = await api.post("user/register", formData);
      if (response.success) {
        toast.success("রেজিস্ট্রেশন সম্পন্ন হয়েছে। অ্যাডমিনের অনুমোদনের পর আপনি লগিন করতে পারবেন।");
        reset();
        setSelectedFile(null);
        setFilePreview(null);
        setAcceptedTerms(false);
      } else {
        toast.error(response?.error);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ??
          error?.message ??
          "একটি অজানা সমস্যা হয়েছে"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const InputField = ({
    label,
    name,
    type = "text",
    placeholder,
    icon: Icon,
    required = false,
    validation = {},
    children,
  }) => (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        {Icon && <Icon className="w-4 h-4" />}
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          {...register(
            name,
            required
              ? { required: `${label} আবশ্যক`, ...validation }
              : validation
          )}
          type={type}
          placeholder={placeholder}
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 ${
            errors[name] ? "border-red-500 bg-red-50" : "hover:border-gray-400"
          }`}
        />
        {children}
      </div>
      {errors[name] && (
        <div className="flex items-center gap-1 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          {errors[name].message}
        </div>
      )}
    </div>
  );

  const SelectField = ({
    label,
    name,
    options,
    icon: Icon,
    required = false,
    valueKey = "id",
    labelKey = "bn_name",
  }) => (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        {Icon && <Icon className="w-4 h-4" />}
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        {...register(name, required ? { required: `${label} আবশ্যক` } : {})}
        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 ${
          errors[name] ? "border-red-500 bg-red-50" : "hover:border-gray-400"
        }`}
      >
        <option value="">নির্বাচন করুন</option>
        {options?.map((option) => (
          <option key={option[valueKey]} value={option[valueKey]}>
            {typeof option[labelKey] === "object"
              ? option[labelKey].bangla
              : option[labelKey]}
          </option>
        ))}
      </select>
      {errors[name] && (
        <div className="flex items-center gap-1 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          {errors[name].message}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-purple-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            নিঃস্বার্থ নবজীবন সংগঠন
          </h1>
          <p className="text-gray-600 mb-2">
            আপনাকে স্বাগতম - রেজিস্ট্রেশন সম্পন্ন করুন
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-sm text-blue-800">
              <strong>দ্রষ্টব্য:</strong> রেজিস্ট্রেশনের পর অ্যাডমিনের অনুমোদন ও
              NNS ID প্রদানের পর আপনি লগিন করতে পারবেন।
            </p>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">শর্তাবলী</h3>
          <div className="prose prose-sm text-gray-700">
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                ভর্তি ফি বাবদ ৫০/- ও মাসিক প্রণামী নূন্যতম ৫০/- ১ম মাসে ১০০/-,
                পরবর্তী মাস হতে নূন্যতম ৫০/- প্রণামী প্রদান করে যেতে হবে।
              </li>
              <li>
                এই প্রণামী প্রদান সম্পূর্ণ নিঃস্বার্থভাবেই প্রদান করে যেতে হবে।
                যা হতে কোনো লভ্যাংশ বা সুযোগ সুবিধা পাবেন না।
              </li>
              <li>
                এই প্রণামী অর্থ সম্পূর্ণ অসহায়, নিঃস্ব, হতদরিদ্র মানুষগুলোর পাশে
                দাঁড়াতে ব্যয় হবে। (সংগঠনের সকল আনুষ্টানিকতা ও সাংগঠনিকতার ব্যয়,
                সংগঠনের উপদেষ্টা-পৃষ্ঠপোষক, সিনিয়র সদস্য ও শুভাকাঙ্খী হতে
                প্রাপ্ত অর্থ দিয়ে পরিচালনা করা হয়)
              </li>
            </ol>
          </div>
          <div className="flex items-start mt-4">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 mr-2"
            />
            <label htmlFor="terms" className="text-sm text-gray-700">
              শর্তাবলী আমি পড়েছি এবং গ্রহণ করছি
            </label>
          </div>
          {!acceptedTerms && (
            <p className="text-red-500 text-sm mt-1">
              শর্তাবলী গ্রহণ করা আবশ্যক
            </p>
          )}
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                  <User className="w-6 h-6 text-primary" />
                  ব্যক্তিগত তথ্য
                </h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Camera className="w-4 h-4" />
                      ছবি নির্বাচন করুন <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        {...register("file", { required: "ছবি আবশ্যক" })}
                        onChange={handleFileChange}
                        accept="image/*"
                        className="hidden"
                        id="file-upload"
                        ref={fileInputRef}
                      />
                      <label
                        htmlFor="file-upload"
                        className={`flex items-center justify-center w-full px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${
                          errors.file
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                        }`}
                      >
                        {filePreview ? (
                          <div className="relative">
                            <img
                              src={filePreview}
                              alt="Preview"
                              className="h-32 w-32 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage();
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="text-center">
                            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">
                              ছবি আপলোড করতে ক্লিক করুন
                            </p>
                            <p className="text-xs text-gray-500">
                              (সর্বোচ্চ 2MB, JPG/PNG)
                            </p>
                          </div>
                        )}
                      </label>
                    </div>
                    {errors.file && (
                      <div className="flex items-center gap-1 text-red-500 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.file.message}
                      </div>
                    )}
                  </div>

                  <InputField
                    label="নাম"
                    name="name"
                    placeholder="আপনার নাম লিখুন"
                    icon={User}
                    required
                  />

                  <InputField
                    label="নাম (ইংরেজিতে)"
                    name="nameEn"
                    placeholder="Your name in English"
                    icon={User}
                  />

                  <InputField
                    label="পিতার নাম"
                    name="father"
                    placeholder="আপনার পিতার নাম লিখুন"
                    icon={User}
                  />

                  <InputField
                    label="মাতার নাম"
                    name="mother"
                    placeholder="আপনার মাতার নাম লিখুন"
                    icon={User}
                  />

                  <InputField
                    label="পেশা"
                    name="occupation"
                    placeholder="আপনার পেশা লিখুন"
                    icon={Briefcase}
                  />

                  <InputField
                    label="ইমেইল"
                    name="email"
                    type="email"
                    placeholder="example@email.com"
                    icon={Mail}
                    validation={{
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "সঠিক ইমেইল ঠিকানা লিখুন",
                      },
                    }}
                  />

                  <InputField
                    label="মোবাইল নাম্বার"
                    name="mobile"
                    placeholder="01XXXXXXXXX"
                    icon={Phone}
                    required
                    validation={{
                      pattern: {
                        value: /^01[3-9]\d{8}$/,
                        message: "সঠিক মোবাইল নাম্বার লিখুন",
                      },
                    }}
                  />
                </div>
              </div>

              {/* Address & Institution */}
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                  <Building className="w-6 h-6 text-primary" />
                  ঠিকানা ও প্রতিষ্ঠান
                </h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <InputField
                    label="প্রতিষ্ঠানের নাম"
                    name="institute"
                    placeholder="বর্তমানে কর্মরত প্রতিষ্ঠানের নাম"
                    icon={Building}
                  />

                  <InputField
                    label="পদবী"
                    name="designation"
                    placeholder="আপনার পদবী"
                    icon={Briefcase}
                  />

                  <InputField
                    label="ফেসবুক প্রোফাইল লিংক"
                    name="facebook"
                    placeholder="https://facebook.com/yourprofile"
                    icon={Facebook}
                  />

                  <SelectField
                    label="বিভাগ"
                    name="divisionId"
                    options={divisions}
                    icon={MapPin}
                    required
                  />

                  <SelectField
                    label="জেলা"
                    name="districtId"
                    options={districts}
                    icon={MapPin}
                    required
                  />

                  <SelectField
                    label="উপজেলা"
                    name="upazilaId"
                    options={upazilas}
                    icon={MapPin}
                    required
                  />

                  {unions.length > 0 && (
                    <SelectField
                      label="ইউনিয়ন"
                      name="unionId"
                      options={unions}
                      icon={MapPin}
                    />
                  )}
                </div>
              </div>

              {/* Security & Identity */}
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                  <Lock className="w-6 h-6 text-primary" />
                  পরিচয়
                </h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <SelectField
                    label="শিক্ষাগত যোগ্যতা"
                    name="educationQualification"
                    options={educationQualification}
                    icon={GraduationCap}
                    required
                    labelKey="name"
                  />

                  <InputField
                    label="NID"
                    name="nid"
                    placeholder="জাতীয় পরিচয়পত্র নাম্বার"
                    icon={User}
                    required
                    validation={{
                      minLength: {
                        value: 10,
                        message: "NID কমপক্ষে ১০ সংখ্যার হতে হবে",
                      },
                    }}
                  />

                  <InputField
                    label="জন্ম তারিখ"
                    name="dob"
                    type="date"
                    icon={Calendar}
                    required
                  />

                  <SelectField
                    label="বৈবাহিক অবস্থা"
                    name="maritalStatus"
                    options={maritalStatus}
                    icon={User}
                    required
                  />

                  {/* Custom Password Field to prevent auto-defocus */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Lock className="w-4 h-4" />
                      পাসওয়ার্ড <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        {...register("password", {
                          required: "পাসওয়ার্ড আবশ্যক",
                          minLength: {
                            value: 6,
                            message: "পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে",
                          },
                        })}
                        type={showPassword ? "text" : "password"}
                        placeholder="নিরাপদ পাসওয়ার্ড"
                        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 ${
                          errors.password
                            ? "border-red-500 bg-red-50"
                            : "hover:border-gray-400"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <div className="flex items-center gap-1 text-red-500 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.password.message}
                      </div>
                    )}
                  </div>

                  {/* Custom Confirm Password Field to prevent auto-defocus */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Lock className="w-4 h-4" />
                      পাসওয়ার্ড পুনরায় লিখুন{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        {...register("confirmPassword", {
                          required: "পাসওয়ার্ড নিশ্চিত করুন",
                          validate: (value) =>
                            value === password || "পাসওয়ার্ড মিলছে না",
                        })}
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="পাসওয়ার্ড পুনরায় লিখুন"
                        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 ${
                          errors.confirmPassword
                            ? "border-red-500 bg-red-50"
                            : "hover:border-gray-400"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <div className="flex items-center gap-1 text-red-500 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.confirmPassword.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-primary" />
                  অতিরিক্ত তথ্য
                </h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <SelectField
                    label="রক্তের গ্রুপ"
                    name="bloodGroup"
                    options={bloodGroup.map((bg) => ({
                      id: bg,
                      bn_name: bg.toUpperCase(),
                    }))}
                    icon={Heart}
                    required
                  />

                  <InputField
                    label="শেষ রক্তদানের তারিখ"
                    name="lastBloodDonateDate"
                    type="date"
                    icon={Calendar}
                  />

                  <SelectField
                    label="রক্তদানে ইচ্ছুক কিনা"
                    name="isAgreeDonateBlood"
                    options={[
                      { id: true, bn_name: "হ্যাঁ" },
                      { id: false, bn_name: "না" },
                    ]}
                    icon={Heart}
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      সাবমিট হচ্ছে...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      সাইন আপ সম্পন্ন করুন
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Signup;
